import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiNodeTree } from '@remixicon/react';
import {
  CopyButton,
  CopyableValue,
  ExternalLink,
  InfraField,
  InfraSection,
  InfraStack,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';
import {
  buildLabSetupScript,
  labContextNameFromCluster,
  LabKubectlSection,
  parseLabKubeconfigFromBase64,
} from './labKubectl';

export const GKEClusterInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];

  const clusterName = annotations['gke.infrastructure/cluster-name'];
  const clusterEndpoint = annotations['gke.infrastructure/cluster-endpoint'];
  const clusterVersion = annotations['gke.infrastructure/cluster-version'];
  const clusterLocation = annotations['gke.infrastructure/cluster-location'];
  const consoleUrl = annotations['gke.infrastructure/console-url'];
  const nodePoolName = annotations['gke.infrastructure/node-pool-name'];
  const nodeMachineType = annotations['gke.infrastructure/node-machine-type'];
  const nodeCount = annotations['gke.infrastructure/node-count'];
  const networkName = annotations['gke.infrastructure/network-name'];
  const subnetName = annotations['gke.infrastructure/subnet-name'];
  const serviceAccount = annotations['gke.infrastructure/service-account'];
  const kubeconfigCommand = annotations['gke.infrastructure/kubeconfig-command'];
  const labAccessNamespace = annotations['gke.infrastructure/lab-access-namespace'];
  const labAccessServiceAccount = annotations['gke.infrastructure/lab-access-service-account'];
  const labKubeconfigBase64 = annotations['gke.infrastructure/lab-kubeconfig-base64'];
  const defaultStorageClass = annotations['gke.infrastructure/default-storage-class'];
  const storageClassType = annotations['gke.infrastructure/storage-class-type'];
  const gcpProjectId = annotations['gcp.infrastructure/project-id'];
  const gcpRegion = annotations['gcp.infrastructure/region'];

  if (templateType !== 'gcp-gke-cluster') {
    return null;
  }

  const labContextName = labContextNameFromCluster(clusterName, environmentName);

  const copyLabSetupScript = () => {
    if (!labKubeconfigBase64) {
      return;
    }
    const parts = parseLabKubeconfigFromBase64(labKubeconfigBase64);
    if (!parts) {
      window.alert(
        'Could not parse lab kubeconfig from this component. Refresh the catalog entry or contact support.',
      );
      return;
    }
    navigator.clipboard.writeText(buildLabSetupScript(parts, labContextName));
  };

  const clusterUrl =
    consoleUrl ||
    `https://console.cloud.google.com/kubernetes/clusters/details/${clusterLocation || gcpRegion}/${clusterName}?project=${gcpProjectId}`;

  return (
    <InfoCard
      title="☸️ GCP GKE Cluster"
      subheader={`Environment: ${environmentName || 'N/A'} | Kubernetes: ${clusterVersion || 'N/A'}`}
      icon={<RiNodeTree size={20} />}
    >
      <InfraStack>
        <InfraSection title="🎯 Cluster Information">
          {clusterName && (
            <InfraField
              label="Cluster Name:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink href={clusterUrl}>{clusterName}</ExternalLink>
                  <CopyButton text={clusterName} />
                </span>
              }
              mono={false}
            />
          )}
          {clusterEndpoint && (
            <CopyableValue label="Cluster Endpoint:" value={clusterEndpoint} />
          )}
          {gcpProjectId && <CopyableValue label="Project ID:" value={gcpProjectId} />}
          {gcpRegion && <InfraField label="Region:" value={gcpRegion} />}
        </InfraSection>

        <InfraSection title="🖥️ Node Pool">
          {nodePoolName && <InfraField label="Node Pool Name:" value={nodePoolName} />}
          {nodeMachineType && <InfraField label="Machine Type:" value={nodeMachineType} />}
          {nodeCount && <InfraField label="Node Count:" value={nodeCount} />}
        </InfraSection>

        <InfraSection title="🌐 Network">
          {networkName && (
            <InfraField
              label="Network Name:"
              value={
                <span className={styles.copyRow}>
                  <ExternalLink
                    href={`https://console.cloud.google.com/networking/networks/details/${networkName}?project=${gcpProjectId}`}
                  >
                    {networkName}
                  </ExternalLink>
                  <CopyButton text={networkName} />
                </span>
              }
              mono={false}
            />
          )}
          {subnetName && <CopyableValue label="Subnet Name:" value={subnetName} />}
          {serviceAccount && <CopyableValue label="Service Account:" value={serviceAccount} />}
        </InfraSection>

        {(defaultStorageClass || storageClassType) && (
          <InfraSection title="💾 Storage">
            {defaultStorageClass && (
              <InfraField label="Default StorageClass:" value={defaultStorageClass} />
            )}
            {storageClassType && <InfraField label="Storage Type:" value={storageClassType} />}
          </InfraSection>
        )}

        {kubeconfigCommand && (
          <InfraSection title="🔧 Access Commands">
            <CopyableValue label="Kubeconfig Command:" value={kubeconfigCommand} />
          </InfraSection>
        )}

        <LabKubectlSection
          cloudLabel="Google"
          labKubeconfigBase64={labKubeconfigBase64}
          labAccessServiceAccount={labAccessServiceAccount}
          labAccessNamespace={labAccessNamespace}
          labContextName={labContextName}
          onCopySetupScript={copyLabSetupScript}
        />
      </InfraStack>
    </InfoCard>
  );
};
